import NiceModal from '@ebay/nice-modal-react';
import { css } from '@emotion/react';

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Badge from 'components/field/Badge';
import Button from 'components/field/Button';
import CardViewer from 'components/field/CardViewer';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import * as F from 'components/form/FormLayout.styled';
import Alert from 'components/global/Alert';
import Card from 'components/global/Card';
import Icon from 'components/global/Icon';
import PageHeader from 'components/global/PageHeader';
import ProgressBar from 'components/global/ProgressBar';
import Skeleton from 'components/global/Skeleton';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import { getOTOPackageList } from 'pages/OTOModules/OTOPackageListSlice';

import { PAYMENT } from 'services/consts';

import { getClientCards } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

import SubscriptionAccountModal from '../SubscriptionAccount/SubscriptionAccountModal';
import { setManageSubscriptionReload } from '../SubscriptionAccount/SubscriptionAccountSlice';
import AddPaymentMethodModal from './AddPaymentMethod/AddPaymentMethodModal';
import { ChangePaymentMethodModal } from './ChangePaymentMethod/ChangePaymentMethodModal';
import * as S from './ManageSubscription.styled';

/**
 * @author @muhammedburakak
 */

const ManageSubscription = () => {
	// Hooks and states declaration
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const axios = useAxiosPrivate();
	const { getDate } = useLocale();
	const largeDefaultColSpan = { md: 12 };

	const user = useSelector((state) => state.user);
	const { otoPackageList: packages } = useSelector((state) => state?.otoPackageList);
	const manageSubscriptionReload = useSelector((state) => state.subscriptionAccount.manageSubscriptionReload);
	const isEnterpriseUser = useSelector((state) => state.user.planName) === 'EnterprisePackage';

	const [currentPackage, setCurrentPackage] = useState({});
	const [data, setData] = useState({});

	useEffect(() => {
		dispatch(getOTOPackageList());
	}, []);

	useEffect(() => {
		setCurrentPackage(packages.find((item) => item.planName === user.currentPlanName));
	}, [packages, user.currentPlanName]);

	useEffect(() => {
		if (user.planName) {
			getManageSubscription();
			dispatch(getClientCards({}));
		}
	}, [user.planName]);

	useEffect(() => {
		if (manageSubscriptionReload) {
			getManageSubscription();
			dispatch(getClientCards({}));
		}
	}, [manageSubscriptionReload]);

	const getManageSubscription = useCallback(async () => {
		try {
			const response = await axios.post(PAYMENT.GET_MANAGE_SUBSCRIPTION, {
				page: 'manage-subscription'
			});

			if (response.status === 200) {
				setData(response.data);
				dispatch(setManageSubscriptionReload(false));
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
		}
	}, [user.planName, manageSubscriptionReload]);

	return (
		<div>
			<S.ManageSubscriptionContainer>
				{data.status === 'waitingPayment' && (
					<Alert
						variant="warning"
						showIcon
						header="subscriptionRenewalFailed"
						content={t('subscriptionRenewalFailedInfoText', {
							cardLastFour: data.cardLastFour
						})}
						ctaButtonLabel="renewSubscription"
						onCtaButtonClick={() =>
							NiceModal.show(SubscriptionAccountModal, {
								renewSubscription: true
							})
						}
						className="my-2"
					/>
				)}

				{data.status === 'suspended' && (
					<Alert
						variant="error"
						showIcon
						header="subscriptionSuspendedTitle"
						content="subscriptionSuspenedInfoText"
						ctaButtonLabel="renewSubscription"
						onCtaButtonClick={() =>
							NiceModal.show(SubscriptionAccountModal, {
								currentPackage,
								selectedPackage: packages.find((item) => item.planName === user.planName),
								selectedPlanType: 'MONTHLY'
							})
						}
						className="my-2"
					/>
				)}

				<PageHeader
					lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/manage-subscription-icon.json.gz"
					title={isEnterpriseUser ? 'paymentManagement' : 'manageSubscription'}
				/>
				{!isEnterpriseUser && (
					<Form className="mb-5">
						<FormItem colSpan={largeDefaultColSpan}>
							<div className="statusForm">
								<div>
									<Typography className="mb-3" color="var(--content-3)">
										subscription
									</Typography>
									{data.status ? (
										<Typography fontWeight="500" className="mb-3">
											{data.status ?? '-'}
										</Typography>
									) : (
										<Skeleton width="50%" height="1rem" className="mb-3" />
									)}
								</div>

								<div>
									<Typography className="mb-3" color="var(--content-3)">
										currentPackage
									</Typography>

									{Object.keys(data).length > 0 ? (
										!data.currentPackageName && data.newPackageName !== 'FreePackage' ? (
											<Typography fontWeight="500" className="mb-3">
												{t('standardManageSubscriptionTitle', {
													packageTitle: t(`${data.newPackageName}.packageTitle`),
													startDate: data.startDate ? getDate(data.startDate) : ''
												})}
											</Typography>
										) : !data.downgradeDate ? (
											<Typography fontWeight="500" className="mb-3">
												{t(`${data.newPackageName}.packageTitle`)}
											</Typography>
										) : null
									) : (
										<Skeleton width="90%" height="1rem" className="mb-3" />
									)}

									{data.currentPackageName && data.newPackageName && data.downgradeDate && (
										<Typography fontWeight="500" className="mb-3">
											{t('downgradeManageSubscriptionTitle', {
												packageTitle: t(`${data.currentPackageName}.packageTitle`),
												newPackageTitle: t(`${data.newPackageName}.packageTitle`),
												endDate: data.endDate
													? getDate(data.endDate)
													: getDate(data.nextBillingDate)
											})}
										</Typography>
									)}
								</div>

								<div>
									<Typography className="mb-3" color="var(--content-3)">
										nextBillingDate
									</Typography>
									{Object.keys(data).length > 0 ? (
										<Typography fontWeight="500" className="mb-3">
											{data.nextBillingDate ? getDate(data.nextBillingDate) : '-'}
										</Typography>
									) : (
										<Skeleton width="50%" height="1rem" className="mb-3" />
									)}
								</div>
							</div>
						</FormItem>
					</Form>
				)}

				<Form className="mb-5">
					<S.ItemContainer>
						{!isEnterpriseUser && (
							<>
								<S.ItemBoxNoBorder className="mt-3 mb-3">
									<Typography style={{ justifyContent: 'center' }} fontWeight="700">
										subscriptionDetails
									</Typography>
								</S.ItemBoxNoBorder>

								<S.ItemBox>
									<Typography fontWeight="500" className="mb-3">
										packageAndBillingPeriod
									</Typography>
									<div style={{ display: 'flex', justifyContent: 'space-between' }}>
										<div>
											{data.newPackageName ? (
												<Typography fontWeight="500" style={{ alignSelf: 'center' }}>
													{t(`${data.newPackageName}.packageTitle`)} - {data.amount}{' '}
													{t(data.currency)} {data.paymentType ? '/' : ''}{' '}
													{data.paymentType ? t(`${data.paymentType}.paymentType`) : ''}
												</Typography>
											) : (
												<Skeleton width="20%" height="2rem" style={{ alignSelf: 'center' }} />
											)}
											{data?.monthlyFreeIntegratorOrder && (
												<div className="my-3">
													<S.InlineTypography>
														<Typography variant="caption" fontWeight="bold" noWrap>
															{data?.monthlyFreeIntegratorOrder} {t('freeShipment')}
														</Typography>
														<Typography
															variant="caption"
															color="var(--content-3)"
															noWrap
															className="mx-1">
															/
														</Typography>
														<Typography variant="caption" color="var(--content-3)" noWrap>
															upgradeAccountPage.monthlyYourOwnRates
														</Typography>
														<Typography
															variant="caption"
															color="var(--content-3)"
															noWrap
															className="mx-1">
															,
														</Typography>
														<Typography variant="caption" fontWeight="bold" noWrap>
															{data?.extraOrderCost} {t(data.currency)}
														</Typography>
														<Typography variant="caption" color="var(--content-3)" noWrap>
															/{t('extraShipment')}
														</Typography>
													</S.InlineTypography>
												</div>
											)}
										</div>

										<div>
											<Button
												label="change"
												disabledTooltip={'renewalFailedButtonDisabledTooltip'}
												disabled={data.status === 'waitingPayment'}
												style={{ width: '10rem' }}
												variant="blue"
												onClick={() =>
													NiceModal.show(SubscriptionAccountModal, {
														manageSubscription: true
													})
												}
											/>
										</div>
									</div>
								</S.ItemBox>

								<F.Line className="line" />
							</>
						)}

						<S.ItemBoxNoBorder className="mt-3 mb-3">
							<Typography style={{ justifyContent: 'center' }} fontWeight="700">
								paymentDetails
							</Typography>
						</S.ItemBoxNoBorder>

						<S.ItemBox>
							<Typography fontWeight="500" className="mb-3">
								primaryPaymentMethod
							</Typography>
							<div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
								<Typography style={{ alignSelf: 'center' }} color="var(--content-3)" noWrap>
									{data.cardLastFour && data.cardBrand ? (
										<CardViewer lastFour={data.cardLastFour} cardBrand={data.cardBrand} />
									) : user?.clientCards?.length > 0 &&
									  data.newPackageName === 'FreePackage' &&
									  data.downgradeDate ? (
										t('youHavePaymentMethod')
									) : (
										t('noPaymentMethodAdded')
									)}
								</Typography>
								<div style={{ display: 'flex', height: 'min-content', gap: '0.5rem' }}>
									<Button
										label="addCard"
										style={{ width: '10rem' }}
										variant="outline"
										onClick={() => NiceModal.show(AddPaymentMethodModal)}
									/>

									{user?.clientCards?.length > 0 && data?.status !== 'waitingPayment' && (
										<Button
											label={
												user.planName !== 'FreePackage' && !user.accountSuspended
													? 'change'
													: 'delete'
											}
											style={{ width: '10rem' }}
											variant={
												user.planName !== 'FreePackage' && !user.accountSuspended
													? 'blue'
													: 'outline'
											}
											onClick={() =>
												NiceModal.show(ChangePaymentMethodModal, {
													subscriptionCardID: data.clientCardID
												})
											}
										/>
									)}

									{data?.status === 'waitingPayment' && (
										<Button
											label="renew"
											style={{ width: '10rem' }}
											variant="blue"
											onClick={() =>
												NiceModal.show(SubscriptionAccountModal, {
													renewSubscription: true
												})
											}
										/>
									)}
								</div>
							</div>
						</S.ItemBox>
					</S.ItemContainer>
				</Form>

				<Card className="mb-5">
					<S.ItemContainer>
						<S.Box>
							<S.InlineTypography className="mb-5">
								<S.BoxTitle>
									<Typography fontWeight="700">NumberOfShipmentsInYourPlan</Typography>
									<Tooltip
										placement="right"
										title={
											isEnterpriseUser
												? 'NumberOfShipmentsInYourPlanEnterpriseTootltip'
												: 'NumberOfShipmentsInYourPlanTootltip'
										}>
										<Icon icon={faCircleInfo} />
									</Tooltip>
									<Badge variant="pill" size="small" color="red" text="new" />
								</S.BoxTitle>
								<S.BoxTitle>
									<Typography color="var(--content-3)">
										{isEnterpriseUser ? t('usageResetEnterprise') : t('usageReset')}
									</Typography>
									<Typography color="var(--content-3)" fontWeight="700">
										{getDate(data?.monthlyFreeShipmentResetDate)}
									</Typography>
								</S.BoxTitle>
							</S.InlineTypography>
							<Card className="mb-3">
								<S.InlineTypography className="mb-3">
									<Typography fontWeight="700">
										{isEnterpriseUser ? t('includedShipmentsEnterprise') : t('includedShipments')}
									</Typography>
									<S.FlexContainer>
										<Typography fontWeight="700" noWrap>
											{data?.givenShipments - data?.remainingShipments}/
										</Typography>
										<Typography noWrap>{data?.givenShipments}</Typography>
									</S.FlexContainer>
								</S.InlineTypography>
								<ProgressBar
									value={
										data?.givenShipments && data?.remainingShipments
											? ((data?.givenShipments - data?.remainingShipments) /
													data?.givenShipments) *
												100
											: 0
									}
									color="var(--blue)"
								/>
							</Card>
							<Alert
								variant="info"
								showIcon
								content={isEnterpriseUser ? 'youGotNumOfShipmentsEnterprise' : 'youGotNumOfShipments'}
							/>
						</S.Box>
					</S.ItemContainer>
				</Card>

				{data.downgradeDate && (
					<Form>
						<S.ItemContainer className="mt-3 mb-5">
							<S.ItemBoxNoBorder className="mt-3 mb-3">
								<Typography style={{ justifyContent: 'center' }} fontWeight="700">
									downgradeRequest
								</Typography>
							</S.ItemBoxNoBorder>

							<S.ItemBox>
								<Typography fontWeight="500" className="mb-3">
									currentPlan
								</Typography>
								<div style={{ display: 'flex', justifyContent: 'space-between' }} className="mb-3">
									<div>
										<Typography fontWeight="500" style={{ alignSelf: 'center' }}>
											{t('downgradeManageSubscriptionTitle', {
												packageTitle: t(`${data.currentPackageName}.packageTitle`),
												newPackageTitle: t(`${data.newPackageName}.packageTitle`),
												endDate: data.endDate
													? getDate(data.endDate)
													: getDate(data.nextBillingDate)
											})}
										</Typography>
										{data?.monthlyFreeIntegratorOrder && (
											<div className="my-3">
												<S.InlineTypography>
													<Typography variant="caption" fontWeight="bold" noWrap>
														{data?.monthlyFreeIntegratorOrder} {t('freeShipment')}
													</Typography>
													<Typography
														variant="caption"
														color="var(--content-3)"
														noWrap
														className="mx-1">
														/
													</Typography>
													<Typography variant="caption" color="var(--content-3)" noWrap>
														upgradeAccountPage.monthlyYourOwnRates
													</Typography>
													<Typography
														variant="caption"
														color="var(--content-3)"
														noWrap
														className="mx-1">
														,
													</Typography>
													<Typography variant="caption" fontWeight="bold" noWrap>
														{data?.extraOrderCost} {t(data.currency)}
													</Typography>
													<Typography variant="caption" color="var(--content-3)" noWrap>
														/{t('extraShipment')}
													</Typography>
												</S.InlineTypography>
											</div>
										)}
									</div>
								</div>
								<F.Line className="line" />

								<Typography fontWeight="500" className="mt-3">
									requestedPlan
								</Typography>
								<div style={{ display: 'flex', justifyContent: 'space-between' }}>
									<Typography fontWeight="500" style={{ alignSelf: 'center' }}>
										{t('requestedPlanTitle', {
											packageTitle: t(`${data.newPackageName}.packageTitle`),
											downgradeDate: data.downgradeDate ? getDate(data.downgradeDate) : ''
										})}
									</Typography>

									<Button
										style={{ width: '15rem' }}
										css={css`
											border-color: #de350b !important;
											background-color: transparent !important;

											> span {
												color: #de350b !important;
											}
											&:hover {
												background-color: #de350b !important;
												color: var(--alwaysWhiteColor) !important;

												> span {
													color: var(--alwaysWhiteColor) !important;
												}
											}
										`}
										label="cancelRequest"
										variant="outline"
										onClick={() => {
											user?.clientCards?.length > 0
												? NiceModal.show(ChangePaymentMethodModal, {
														subscriptionCardID: data.clientCardID,
														cancelDowngradeRequest: true
													})
												: NiceModal.show(AddPaymentMethodModal, {
														cancelDowngradeRequest: true
													});
										}}
									/>
								</div>
							</S.ItemBox>
						</S.ItemContainer>
					</Form>
				)}
			</S.ManageSubscriptionContainer>
		</div>
	);
};

export default ManageSubscription;
